<template>
  <div>
    <!-- Draw a line separating these groups from the rest of the infowindow -->
    <div v-if="typesUsed.length">
      <div class="spacer" />
      <hr>
    </div>
    <v-expansion-panels
      v-model="typePanel"
      multiple
    >
      <!-- Panels for each Comp type -->
      <v-expansion-panel
        v-for="{type, label} in typesUsed"
        :key="type"
        :value="type"
        :data-testid="`comp-type-panel-${label}`"
        class="comp-type-panel"
        elevation="0"
      >
        <v-expansion-panel-title class="subheading">
          {{ label }}
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-expansion-panels
            v-model="openedPanels"
            multiple
          >
            <!-- Panels for each Comp within a type -->
            <v-expansion-panel
              v-for="(comp, index) in groupedComps[type]"
              :key="`${index}${comp.id}`"
              :data-testid="`comp-panel-${comp.id}`"
              :value="comp.id"
              class="comp-panel"
              elevation="0"
            >
              <v-expansion-panel-title>
                <template #default="{ expanded }">
                  <CaretIcon
                    class="caret"
                    data-testid="comp-group-caret-icon"
                    :class="{ expanded }"
                  />
                  <div
                    v-if="!editing"
                    class="display-field-text comp-label"
                  >
                    <a
                      v-if="comp.link"
                      :href="comp.link.url"
                      target="_blank"
                      class="display-field-text left"
                      :class="{'show-text-link': expanded}"
                      @click.stop
                    >
                      <v-icon
                        v-if="comp.type === 'Active Lease Deal' && getCompStage(comp)"
                        class="rfp-stage-icon"
                        color="np-black"
                        size="medium"
                      >
                        {{ `mdi-numeric-${getCompStage(comp)}-circle-outline` }}
                      </v-icon>
                      {{ comp.name }}
                    </a>
                    <div
                      v-else
                      class="display-field-text left"
                      :class="{'show-text': expanded}"
                    >
                      {{ comp.name }}
                    </div>
                    <span
                      v-if="!expanded"
                      class="title-premise-area right"
                      :class="getPastExpirationDate(comp) ? 'text-decoration-line-through' : ''"
                      data-testid="comp-premise-area"
                    >
                      {{ getCompPremiseAreaLabel(comp) }}
                    </span>
                  </div>
                  <div
                    v-else
                    class="editable-comp-name-row"
                  >
                    <v-text-field
                      :model-value="getCompName(type, index)"
                      :label="getCompPlaceholder(comp.type)"
                      :placeholder="getCompPlaceholder(comp.type)"
                      hide-details="auto"
                      single-line
                      density="compact"
                      data-testid="comp-name-input"
                      bg-color="#ebebeb"
                      class="slim"
                      elevation="0"
                      @change="updateCompName($event, comp)"
                      @click.stop
                      @keyup.space.prevent
                    />
                    <TrashIcon
                      data-testid="delete-comp-button"
                      class="delete-button"
                      @click.stop="$emit('deleteComp', comp)"
                    />
                  </div>
                </template>
              </v-expansion-panel-title>
              <!-- eager is needed to force rendering even when hidden for testing -->
              <v-expansion-panel-text eager>
                <DisplayFields
                  :display-fields="comp.displayFields ?? []"
                  class="comp-groups-display-fields"
                  :editing="editing"
                  :comp-id="comp.id"
                  :comp-group-type="comp.type"
                  @addDisplayField="(newField, index) => $emit('addCompField', comp, newField, index)"
                  @updateDisplayField="(newField, oldField) => $emit('updateCompField', comp, newField, oldField)"
                  @deleteDisplayField="(oldField) => $emit('deleteCompField', comp, oldField)"
                />
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
// Components
import CaretIcon from '@/components/svgs/CaretIcon.vue';
import TrashIcon from '@/components/svgs/TrashIcon.vue';
import DisplayFields from '../DisplayFields.vue';

// Tools & Services
import {group} from '@/lib/ReusableFunctions/Object.js';
import {InfoWindowContent, extractField} from '@/lib/classes/InfoWindowContent';
import {getCompPresentation} from '@/services/ContentPresentation';
import {formatWithCommas} from '@/lib/ReusableFunctions/Formatters';
import {computed} from 'vue';
import {mapMutations, mapState} from 'vuex';

export default {
  name: 'CompGroups',
  components: {
    CaretIcon,
    TrashIcon,
    DisplayFields,
  },
  provide() {
    return {
      leftMargin: computed(() => this.editing ? '20px' : '14px'),
    };
  },
  props: {
    comps: {
      type: Array,
      default: () => [],
    },
    editing: {
      type: Boolean,
      default: false,
    },
    infoWindowContent: {
      type: InfoWindowContent,
      default: null,
    },
  },
  emits: ['addComp', 'updateCompField', 'addCompField', 'deleteCompField', 'deleteComp', 'updateCompName'],
  data() {
    return {
      typePanel: [],
      compNames: {},
      openedPanels: [], // This helps keep track of which panels should automatically opened when a comp is added
      compIds: new Set(), // A set of all compIds currently part of `comps`
    };
  },
  computed: {
    ...mapState('infowindow', {compTypeOrder: 'orderedCompTypes'}),
    orderedTypes() {
      return this.compTypeOrder[this.infoWindowContent?.type] ?? this.compTypeOrder.default;
    },
    typesUsed() {
      return this.orderedTypes.filter(({type}) => type in this.groupedComps);
    },
    groupedComps() {
      const grouped = group(this.comps, (comp) => {
        if (comp.type === 'Utility') {
          return 'Utilities';
        }
        return comp.type;
      });
      // any time the groups are rebuilt, then the corresponding name values need to be
      // rebuilt also
      this.buildCompNames(grouped);
      return grouped;
    },
  },
  watch: {
    comps: {
      handler(newVal) {
        this.compIds = new Set(newVal.map(({id, type}) => {
          if (!this.compIds.has(id)) {
            this.openedPanels.push(id); // New comps start expanded
            const typeConfig = this.orderedTypes.find((t) => t.type === type);
            if (!this.typePanel.includes(type) && !typeConfig?.defaultCollapsed) {
              this.typePanel.push(type);
            }
          }
          return id;
        }));
      },
      deep: true,
    },
  },
  mounted() {
    this.typePanel = this.orderedTypes.filter((t) => !t.defaultCollapsed).map(({type}) => type);
    this.comps.forEach(({id}) => this.compIds.add(id));
  },
  methods: {
    ...mapMutations(['setSnack']),
    buildCompNames(fromGroup) {
      Object.keys(fromGroup).forEach((type) => {
        fromGroup[type].forEach((comp, index) => {
          this.compNames[type] = this.compNames[type] ?? [];
          this.compNames[type][index] = comp.name;
        });
      });
    },
    getCompName(compType, compIndex) {
      return this.compNames[compType][compIndex];
    },
    getCompPlaceholder(compType) {
      return getCompPresentation(compType).getPlaceholderText();
    },
    getCompPremiseAreaLabel(comp) {
      const premiseArea = extractField(comp, 'Premise Area')?.value ?? null;
      return premiseArea ? `${formatWithCommas(premiseArea)} SF` : '';
    },
    updateCompName(domEvent, comp) {
      this.$emit('updateCompName', comp, domEvent.target.value);
    },
    getPastExpirationDate(comp) {
      const expirationDate = extractField(comp, 'Expiration Date')?.value ?? null;
      return expirationDate && (new Date(expirationDate) < new Date());
    },
    getCompStage(comp) {
      const stageValue = extractField(comp, 'Lease Deal Stage')?.value;
      const match = stageValue?.match(/\d+/); // Get stage number
      return match ? match[0] : null;
    },
  },
};
</script>
<style lang="scss" scoped>
.spacer {
  margin-top: 5px;
  padding-bottom: 15px;
}
.comp-groups-display-fields {
  :deep(.displayFieldsWrapper) {
    width: inherit;
    margin-left: -5px;
  }

  :deep(.displayFieldValue) {
    margin-left: -26px;
    max-width: 195px;
    margin-right: -3px;

    :deep(.date-row-date-picker) {
      width: 192px;
    }
  }

  :deep(.displayFieldLabel) {
    max-width: 160px;
    margin-right: 25px;

    :deep(.v-field__input) {
      margin-left: -2px;
    }
  }

  :deep(.displayLabelIcon) {
    right: 33px;
  }

  :deep(.new-field-value-wrapper) {
    margin-left: -3px;
    max-width: 195px;
  }

  :deep(.new-field-label-wrapper) {
    max-width: 180.5px;
    margin-right: 2px;
  }
}
.editable-comp-name-row {
  width: 385px;
  margin-right: -3px;
  position: relative;
  font-family: main.$fontPrimary;
  margin-bottom: 2px;
  :deep(.v-field__input) {
    color: rgb(61, 70, 68);
  }
  .delete-button {
    position: absolute;
    right: -16px;
    top: 1px;
    width: 20px;
    height: 20px;
  }
}

.caret {
  flex: 0 0 5%;
  height: 22px;
  width: 20px;
  margin-left: 0;
  transform: scale(1.5) translateX(-6px) translateY(-1px);
  &.expanded {
    transform: rotate(180deg) scale(1.5) translateX(5px);
  }
}

.title-premise-area {
  margin-right: 5px;
}

// eslint-disable vue-scoped-css/no-unused-selector
.v-expansion-panel {
  margin-bottom: 2px;
  margin-top: 0;
  .v-expansion-panel-title {
    height: 22px;
    padding: 0;
    min-height: 22px;
    margin-top: 5px;
    :deep(.v-expansion-panel-title__overlay){
      opacity: 0;
    }
    :deep(.v-expansion-panel-title__icon) {
      transform: translateX(4px);
    }
    :deep(.v-field__field) {
      background-color: #ebebeb;

      &:hover {
        background-color: #ebebeb;
      }
    }
  }
  .v-expansion-panel-text {
    :deep(.v-expansion-panel-text__wrapper) {
      padding: 0px 6px 0px;
    }
  }
  &.comp-type-panel::after {
    opacity: 1;
  }
  &.comp-panel::after {
    border: none;
  }
  &.comp-panel {
    :deep(.v-expansion-panel-title__icon) {
      margin-right: 6px;
      display: none;
    }
  }
}

.slim {
  margin-left: 3px;
  margin-right: 3px;
  width: 97%;
  :deep(.v-field) {
    font-size: 13px;
    border-radius: 2px;
  }
  :deep(.v-field__input) {
    min-height: 0;
    padding: 2px 3px;
  }
  :deep(.v-field__outline) {
    display: none;
  }
  :deep(.v-label.v-field-label) {
    font-size: unset;
    margin: unset;
    padding: 1px 3px;
  }
}

.comp-label {
  width: 95%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  .left {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding-right: 3px;
    flex: 0 1 auto;
  }
  .show-text {
    overflow: visible;
    white-space: normal;
    display: block;
    margin-right: 5px;
  }
  .show-text-link {
    overflow: visible;
    white-space: normal;
    display: block;
    margin-right: 70px;
  }
  .right {
    flex: 0 0 auto;
  }
}

a {
  padding: 0;
  font-size: 13px;
  text-decoration: none;
  z-index: 12;
  font-family: main.$fontPrimary;
  &:hover {
    color: main.$linkBlue !important;
    text-decoration: underline;
  }
}

.rfp-stage-icon {
  margin-right: 2px;
  vertical-align: text-bottom;
}
</style>
